import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './RecruiterProfile.css';
import {
  User,
  Mail,
  Calendar,
  Loader2,
  AlertCircle,
  Shield,
  CheckCircle2,
  UserCircle,
} from 'lucide-react';

const RecruiterProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    const recruiterId = localStorage.getItem('recruiterId');

    if (!recruiterId) {
      // Handle case where ID is missing (e.g. redirect to login)
      setLoading(false);
      return;
    }

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/recruiter/profile/${recruiterId}`);
      setProfile(res.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching profile:', error);
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'R';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0][0].toUpperCase();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return 'Invalid Date';
    }
  };

  /* ── Loading State ── */
  if (loading) {
    return (
      <div className="rp-root">
        <div className="rp-loading">
          <div className="rp-loading-card">
            <Loader2 className="rp-spin" size={36} />
            <span>Loading profile…</span>
          </div>
        </div>
      </div>
    );
  }

  /* ── No Profile State ── */
  if (!profile) {
    return (
      <div className="rp-root">
        <div className="rp-loading">
          <div className="rp-loading-card rp-error-card">
            <AlertCircle size={40} className="rp-error-icon" />
            <span>Could not load profile. Please try logging in again.</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rp-root">
      {/* Background blobs */}
      <div className="rp-bg-blob rp-blob-1" />
      <div className="rp-bg-blob rp-blob-2" />

      {/* ── Header ── */}
      <div className="rp-header">
        <div className="rp-header-left">
          <div className="rp-title-icon">
            <UserCircle size={26} />
          </div>
          <div>
            <h1 className="rp-title">My Profile</h1>
            <p className="rp-subtitle">View your account details</p>
          </div>
        </div>
      </div>

      {/* ── Main Content ── */}
      <div className="rp-content">
        {/* Hero Card with Avatar */}
        <div className="rp-hero-card">
          <div className="rp-hero-bg" />

          <div className="rp-hero-avatar-wrap">
            <div className="rp-hero-avatar">{getInitials(profile.username)}</div>
            <div className="rp-verified-badge" title="Verified Recruiter">
              <CheckCircle2 size={14} />
            </div>
          </div>

          <div className="rp-hero-info">
            <h2 className="rp-hero-name">{profile.username || 'Recruiter'}</h2>
            <p className="rp-hero-email">
              <Mail size={13} /> {profile.email || 'No email'}
            </p>
            <span className="rp-role-badge">
              <Shield size={11} /> Recruiter
            </span>
          </div>
        </div>

        {/* Details Section */}
        <div className="rp-details-card">
          <div className="rp-section-header">
            <div className="rp-section-icon">
              <User size={18} />
            </div>
            <h3>Account Information</h3>
          </div>

          <div className="rp-details-grid">
            <div className="rp-detail-item">
              <div className="rp-detail-icon">
                <User size={16} />
              </div>
              <div className="rp-detail-content">
                <label>Username / Name</label>
                <span className="rp-detail-value">{profile.username || 'N/A'}</span>
              </div>
            </div>

            <div className="rp-detail-item">
              <div className="rp-detail-icon rp-icon-purple">
                <Mail size={16} />
              </div>
              <div className="rp-detail-content">
                <label>Email Address</label>
                <span className="rp-detail-value">{profile.email || 'N/A'}</span>
              </div>
            </div>

            <div className="rp-detail-item">
              <div className="rp-detail-icon rp-icon-amber">
                <Calendar size={16} />
              </div>
              <div className="rp-detail-content">
                <label>Account Created</label>
                <span className="rp-detail-value">{formatDate(profile.createdAt)}</span>
              </div>
            </div>

            <div className="rp-detail-item">
              <div className="rp-detail-icon rp-icon-green">
                <Shield size={16} />
              </div>
              <div className="rp-detail-content">
                <label>Account Type</label>
                <span className="rp-detail-value">
                  <span className="rp-status-pill">
                    <CheckCircle2 size={11} /> Active Recruiter
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecruiterProfile;